import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CssBaseline } from '@mui/material';

import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedFeature from './components/ProtectedFeature';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ContentLibrary from './pages/ContentLibrary';
import Tags from './pages/Tags';
import Upload from './pages/Upload';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Profile from './pages/Profile';

// Fleet Management Pages
import {
  FleetDashboard,
  FleetDevices,
  FleetPlayerHealth,
  FleetPlayerDeviceDetail,
  FleetProvisioning,
  FleetOrganizations,
  FleetSites,
  FleetAlerts,
} from './pages/Fleet';

// User Management Pages
import { Users as ManagementUsers, Roles as ManagementRoles, RoleDetail as ManagementRoleDetail, AuditLog as ManagementAuditLog } from './pages/Management';
import AcceptInvite from './pages/AcceptInvite';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// System Pages
import { SystemStatus } from './pages/System';

// Scheduling Pages
import {
  SchedulingDashboard,
  SchedulingSchedules,
  SchedulingSequences,
  SchedulingSegments,
  SchedulingPlaylists,
  SchedulingChannels,
  SchedulingZones,
} from './pages/Scheduling';

// AI Generation Pages
import { SongForge } from './pages/AIGeneration';

function App() {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <CssBaseline />
      <Routes>
        {/* Public routes - no auth required */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/invite/:token" element={<AcceptInvite />} />

        {/* Protected routes - wrapped in Layout */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/content" element={<ContentLibrary />} />
                  <Route path="/content/tags" element={<Tags />} />

                  {/* Upload requires admin or editor role */}
                  <Route
                    path="/upload"
                    element={
                      <ProtectedRoute requiredRoles={['admin', 'editor']}>
                        <Upload />
                      </ProtectedRoute>
                    }
                  />

                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/profile" element={<Profile />} />

                  {/* Settings requires admin role */}
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <Settings />
                      </ProtectedRoute>
                    }
                  />

                  {/* Fleet Management Routes */}
                  <Route path="/fleet" element={<FleetDashboard />} />
                  <Route path="/fleet/devices" element={<FleetDevices />} />
                  <Route path="/fleet/devices/:id" element={<FleetDevices />} />
                  <Route path="/fleet/player-health" element={<FleetPlayerHealth />} />
                  <Route path="/fleet/player-health/:deviceId" element={<FleetPlayerDeviceDetail />} />
                  <Route path="/fleet/provisioning" element={<FleetProvisioning />} />
                  <Route path="/fleet/sites" element={<FleetSites />} />
                  <Route path="/fleet/organizations" element={<FleetOrganizations />} />
                  <Route path="/fleet/alerts" element={<FleetAlerts />} />

                  {/* User Management Routes */}
                  <Route
                    path="/management/users"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <ManagementUsers />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/management/roles"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <ManagementRoles />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/management/roles/:id"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <ManagementRoleDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/management/audit"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <ManagementAuditLog />
                      </ProtectedRoute>
                    }
                  />

                  {/* System Routes */}
                  <Route
                    path="/system/status"
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <SystemStatus />
                      </ProtectedRoute>
                    }
                  />

                  {/* Scheduling Routes */}
                  <Route path="/scheduling" element={<SchedulingDashboard />} />
                  <Route path="/scheduling/schedules" element={<SchedulingSchedules />} />
                  <Route path="/scheduling/sequences" element={<SchedulingSequences />} />
                  <Route path="/scheduling/segments" element={<SchedulingSegments />} />
                  <Route path="/scheduling/playlists" element={<SchedulingPlaylists />} />
                  <Route path="/scheduling/channels" element={<SchedulingChannels />} />
                  <Route path="/scheduling/zones" element={<SchedulingZones />} />

                  {/* SongForge Routes - requires songforge:access permission */}
                  <Route
                    path="/songforge"
                    element={
                      <ProtectedFeature feature="SONGFORGE">
                        <SongForge />
                      </ProtectedFeature>
                    }
                  />
                  <Route path="/songforge/*" element={<Navigate to="/songforge" replace />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Box>
  );
}

export default App;
